import {GoogleAnalytics} from '@next/third-parties/google';

import {useTracking} from './useTracking';

/**
 * A tracking component that conditionally loads Google Analytics based on user consent.
 * This component integrates with the `useTracking` hook to check whether the user has granted
 * consent for analytics tracking. If consent is given under `detailedConsent.Analytics.consented`,
 * it renders the `GoogleAnalytics` component with the configured GA ID. If consent is not granted,
 * it returns `true` (a React no-op), effectively rendering nothing.
 * This ensures that analytics scripts are only loaded when the user explicitly allows them,
 * supporting privacy compliance requirements such as GDPR or CCPA.
 *
 * @returns A JSX element rendering the Google Analytics integration if consent is granted, otherwise `true` (rendering nothing).
 *
 * @example
 * ```tsx
 * <Tracking />
 * // Renders <GoogleAnalytics gaId="..."/> only if user consent is given
 * ```
 */
const Tracking = () => {
    const {consent} = useTracking();

    return consent.statistics ? (
        <GoogleAnalytics gaId="G-FRW0F7YBVQ" />
    ) : true;
};

Tracking.displayName = 'Tracking';

export {Tracking};